/**
 * Custom Stock Alerts (back in stock)
 * -----------------------------------
 * Drives <custom-stock-alert> (see snippets/custom-stock-alert.liquid):
 *   - A small bell is rendered (in Liquid) on every out-of-stock size pill of the
 *     variant picker. Clicking a bell opens a side drawer ("Stock Alerts"), pre-selecting
 *     the size that was clicked, WITHOUT selecting the (unavailable) radio.
 *   - Inside the drawer the shopper picks an out-of-stock size + enters an email and is
 *     notified. Submission posts to Shopify's contact form (AJAX), so the store owner
 *     receives the request with no app required. A `stock-alert:submit` event is also
 *     dispatched so a back-in-stock app can integrate if one is installed.
 *   - The drawer is moved to #Portal (body level) so position:fixed isn't clipped by a
 *     transformed/sticky ancestor in the product info column.
 *
 * Non-invasive: bell clicks are handled with a single delegated listener; the page
 * scroll-lock class is always released on close and on disconnect.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('custom-stock-alert')) return;

  // Registry so a delegated bell click can find the drawer instance it controls.
  var registry = (window.__customStockAlerts = window.__customStockAlerts || {});

  // One delegated listener for every bell on the page (survives variant re-renders).
  function openFromBell(event, bell) {
    // Stop the parent <label> from selecting the (unavailable) radio.
    event.preventDefault();
    event.stopPropagation();
    var instance = registry[bell.getAttribute('data-sa-target')];
    if (instance) instance.open(bell.getAttribute('data-size'));
  }

  if (!window.__customStockAlertBellBound) {
    window.__customStockAlertBellBound = true;
    document.addEventListener(
      'click',
      function (event) {
        var bell = event.target.closest('[data-stock-alert-trigger]');
        if (bell) openFromBell(event, bell);
      },
      true
    );
    document.addEventListener(
      'keydown',
      function (event) {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        var bell = event.target.closest('[data-stock-alert-trigger]');
        if (bell) openFromBell(event, bell);
      },
      true
    );
  }

  class CustomStockAlert extends HTMLElement {
    connectedCallback() {
      if (!this.drawer) this.drawer = this.querySelector('[data-stock-alert-drawer]');
      if (!this.drawer) return;

      this.drawerId = this.drawer.id;
      registry[this.drawerId] = this;

      // Portal the drawer to body level (re-attach the cached node on reconnect).
      var portal = document.getElementById('Portal') || document.body;
      if (this.drawer.parentElement !== portal) portal.appendChild(this.drawer);
      this.drawer.removeAttribute('hidden');

      this.sizesBox = this.drawer.querySelector('[data-stock-alert-sizes]');
      this.form = this.drawer.querySelector('[data-stock-alert-form]');
      this.sizeInput = this.drawer.querySelector('[data-stock-alert-size-input]');
      this.bodyInput = this.drawer.querySelector('[data-stock-alert-body-input]');
      this.message = this.drawer.querySelector('[data-stock-alert-message]');
      this.submitBtn = this.drawer.querySelector('[data-stock-alert-submit]');

      this._buildSizes();

      if (this._bound) return;
      this._bound = true;
      this._onKeydown = this._onKeydown.bind(this);

      this.drawer.querySelectorAll('[data-stock-alert-close]').forEach(
        function (el) {
          el.addEventListener('click', this.close.bind(this));
        }.bind(this)
      );

      // Size selection inside the drawer.
      if (this.sizesBox) {
        this.sizesBox.addEventListener('click', function (event) {
          var pill = event.target.closest('[data-stock-alert-size]');
          if (pill) this._selectSize(pill);
        }.bind(this));
      }

      if (this.form) this.form.addEventListener('submit', this._onSubmit.bind(this));
    }

    disconnectedCallback() {
      document.body.classList.remove('sa-lock');
      if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
      if (this.drawerId && registry[this.drawerId] === this) delete registry[this.drawerId];
      if (this.drawer) this.drawer.remove();
    }

    // Build the drawer's size buttons from the out-of-stock bells in this section,
    // so the list always matches what's actually unavailable (DRY, no double source).
    _buildSizes() {
      if (!this.sizesBox) return;
      var bells = document.querySelectorAll('[data-stock-alert-trigger][data-sa-target="' + this.drawerId + '"]');
      var seen = {};
      var html = '';
      bells.forEach(function (bell) {
        var size = bell.getAttribute('data-size') || '';
        if (!size || seen[size]) return;
        seen[size] = true;
        var label = bell.getAttribute('data-size-label') || size;
        html +=
          '<button type="button" class="stock-alert__size" data-stock-alert-size data-size="' +
          size.replace(/"/g, '&quot;') +
          '">' +
          label +
          '</button>';
      });
      this.sizesBox.innerHTML = html;
    }

    open(size) {
      this._buildSizes();
      this._resetMessage();
      // Pre-select the size that was clicked (or the first one).
      var target = null;
      if (this.sizesBox) {
        var pills = this.sizesBox.querySelectorAll('[data-stock-alert-size]');
        pills.forEach(function (p) {
          if (size != null && p.getAttribute('data-size') === size) target = p;
        });
        if (!target && pills.length) target = pills[0];
      }
      if (target) this._selectSize(target);

      this.drawer.classList.add('is-open');
      document.body.classList.add('sa-lock');
      document.addEventListener('keydown', this._onKeydown);
      var closeBtn = this.drawer.querySelector('.stock-alert__close');
      if (closeBtn) closeBtn.focus();
    }

    close() {
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('sa-lock');
      document.removeEventListener('keydown', this._onKeydown);
    }

    _onKeydown(event) {
      if (event.key === 'Escape') this.close();
    }

    _selectSize(pill) {
      if (!this.sizesBox) return;
      this.sizesBox.querySelectorAll('[data-stock-alert-size]').forEach(function (p) {
        p.classList.toggle('is-selected', p === pill);
      });
      var size = pill.getAttribute('data-size') || '';
      if (this.sizeInput) this.sizeInput.value = size;
      this._refreshBody(size);
    }

    _refreshBody(size) {
      if (!this.bodyInput) return;
      var tpl = this.bodyInput.getAttribute('data-template') || '';
      this.bodyInput.value = tpl.replace('[size]', size || '');
    }

    _resetMessage() {
      if (this.message) {
        this.message.hidden = true;
        this.message.textContent = '';
        this.message.classList.remove('stock-alert__message--error');
      }
      if (this.form) this.form.hidden = false;
    }

    _showMessage(text, isError) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.hidden = false;
      this.message.classList.toggle('stock-alert__message--error', !!isError);
    }

    _onSubmit(event) {
      event.preventDefault();
      var form = this.form;
      var email = form.querySelector('[type="email"]');
      var size = this.sizeInput ? this.sizeInput.value : '';

      if (!size) {
        if (this.sizesBox) {
          this.sizesBox.classList.add('stock-alert__sizes--shake');
          setTimeout(function () {
            this.sizesBox.classList.remove('stock-alert__sizes--shake');
          }.bind(this), 400);
        }
        return;
      }
      if (email && !email.checkValidity()) {
        email.reportValidity();
        return;
      }

      // Let any installed back-in-stock app pick this up.
      this.dispatchEvent(
        new CustomEvent('stock-alert:submit', {
          bubbles: true,
          detail: {
            productId: form.getAttribute('data-product-id'),
            productTitle: form.getAttribute('data-product-title'),
            size: size,
            email: email ? email.value : '',
          },
        })
      );

      if (this.submitBtn) this.submitBtn.classList.add('is-loading');

      // Post to Shopify's contact form so the store owner is emailed (no app needed).
      fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      })
        .then(function (res) {
          return res.ok;
        })
        .catch(function () {
          return false;
        })
        .then(function (ok) {
          if (this.submitBtn) this.submitBtn.classList.remove('is-loading');
          if (ok) {
            form.hidden = true;
            this._showMessage(form.getAttribute('data-success') || 'Thanks!', false);
            form.reset();
          } else {
            this._showMessage(form.getAttribute('data-error') || 'Something went wrong. Please try again.', true);
          }
        }.bind(this));
    }
  }

  customElements.define('custom-stock-alert', CustomStockAlert);
})();
